import { useRef, useState } from 'react'

import { FloatingPanel } from './FloatingPanel'

interface Props {
  value?: string
  onChange: (v: string) => void
  label?: string
  placeholder?: string
  presets?: string[]                 // optional swatches
}

const DEFAULT_PRESETS = [
  '#0f172a', '#1e293b', '#334155', '#64748b', '#94a3b8',
  '#2563eb', '#7c3aed', '#ec4899', '#f43f5e', '#f59e0b',
  '#10b981', '#22d3ee', '#0ea5e9', '#8b5cf6', '#ffffff',
]

// Page-theme CSS variables exposed at runtime by theme.ts. Selecting one
// of these makes the field track the page's current palette instead of
// pinning to a fixed hex value.
const THEME_TOKENS: Array<{ name: string; value: string }> = [
  { name: 'Primary',        value: 'var(--pg-primary)' },
  { name: 'Accent',         value: 'var(--pg-accent)' },
  { name: 'Text',           value: 'var(--pg-text)' },
  { name: 'Text secondary', value: 'var(--pg-text-secondary)' },
  { name: 'Background',     value: 'var(--pg-bg)' },
  { name: 'Surface',        value: 'var(--pg-surface)' },
  { name: 'Border',         value: 'var(--pg-border)' },
  { name: 'Error',          value: 'var(--pg-error)' },
]

export function ColorPickerField({ value, onChange, label, placeholder = '#ffffff', presets = DEFAULT_PRESETS }: Props) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const swatch: string = isHex(value)
    ? value!
    : (typeof value === 'string' && value.startsWith('var(') ? '#cccccc' : '#ffffff')
  return (
    <div style={{ display: 'grid', gap: 6, position: 'relative' }}>
      {label && <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</label>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          ref={anchorRef}
          type="button"
          aria-label="Choose color"
          onClick={() => setOpen(o => !o)}
          style={{
            width: 28, height: 28, borderRadius: 4,
            border: '1px solid var(--color-border)',
            background: swatch, cursor: 'pointer', flexShrink: 0,
          }}
        />
        <input
          type="text"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1, padding: '6px 8px', fontSize: 14,
            border: '1px solid var(--color-border)', borderRadius: 4,
            background: 'var(--color-bg)', color: 'var(--color-text)',
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          }}
        />
      </div>
      {open && (
        <FloatingPanel anchorRef={anchorRef} align="left" width={216} onClose={() => setOpen(false)}>
        <div
          style={{
            background: 'var(--color-bg)', border: '1px solid var(--color-border)',
            borderRadius: 6, padding: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
          }}
        >
          <input
            type="color"
            value={isHex(value) ? (value as string) : '#ffffff'}
            onChange={e => onChange(e.target.value)}
            style={{ width: 200, height: 32, border: 'none', cursor: 'pointer' }}
          />
          <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap', width: 200 }}>
            <button
              type="button"
              onClick={() => { onChange('transparent'); setOpen(false) }}
              title="Transparent"
              style={{
                flex: '1 1 100%',
                padding: '4px 6px', fontSize: 13,
                border: '1px solid var(--color-border)', borderRadius: 4,
                background: 'repeating-conic-gradient(#cbd5e1 0% 25%, #f8fafc 0% 50%) 50% / 8px 8px',
                color: '#0f172a', cursor: 'pointer', fontWeight: 600,
              }}
            >Transparent</button>
            {THEME_TOKENS.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => { onChange(t.value); setOpen(false) }}
                title={`${t.name} (${t.value})`}
                style={{
                  width: 22, height: 22, borderRadius: 4,
                  border: '1px solid var(--color-border)',
                  background: t.value, cursor: 'pointer',
                }}
              />
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4, marginTop: 6, width: 200 }}>
            {presets.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => { onChange(p); setOpen(false) }}
                title={p}
                style={{
                  width: '100%', aspectRatio: '1 / 1',
                  border: '1px solid var(--color-border)', background: p,
                  borderRadius: 3, cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </div>
        </FloatingPanel>
      )}
    </div>
  )
}

function isHex(v: string | undefined): boolean {
  return typeof v === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(v)
}
